import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { environment } from "./environment";
import { getToken, useAuth } from "./App";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import {
  CopingSkillsModals,
  ClinicalSpecialtiesModals,
  DocumentationMethodsModals,
  ModalityModals,
  ModalityStepsModals,
  ActivityModals,
  SubActivitiesModals
} from "./components/SettingsModals";

const Settings = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Check if user is admin (role_id 1) or super admin (role_id 3)
  const isAdminOrSuperAdmin = user && (user.role_id === 1 || user.role_id === 3);
  
  // Tab state - check URL parameter for initial tab
  const [activeTab, setActiveTab] = useState(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'documentation-methods') return 'documentation-methods';
    if (tabParam === 'modality') return 'modality';
    if (tabParam === 'modality-steps') return 'modality-steps';
    if (tabParam === 'activity') return 'activity';
    if (tabParam === 'sub-activities') return 'sub-activities';
    return 'coping-skills';
  });

  // Redirect non-admin users away from admin-only tabs
  useEffect(() => {
    if (!isAdminOrSuperAdmin && (activeTab === 'modality' || activeTab === 'modality-steps' || activeTab === 'activity' || activeTab === 'sub-activities')) {
      setActiveTab('coping-skills');
    }
  }, [isAdminOrSuperAdmin, activeTab]);

  // Coping Skills state
  const [copingSkills, setCopingSkills] = useState([]);
  const [copingSkillsLoading, setCopingSkillsLoading] = useState(false);
  const [copingSkillsError, setCopingSkillsError] = useState("");
  const [showCopingSkillsModal, setShowCopingSkillsModal] = useState(false);
  const [showEditCopingSkillsModal, setShowEditCopingSkillsModal] = useState(false);
  const [selectedCopingSkill, setSelectedCopingSkill] = useState(null);
  const [copingSkillsForm, setCopingSkillsForm] = useState({
    short_description: "",
    long_description: ""
  });
  const [copingSkillsFormLoading, setCopingSkillsFormLoading] = useState(false);
  const [copingSkillsFormError, setCopingSkillsFormError] = useState("");
  const [showDeleteCopingSkillsModal, setShowDeleteCopingSkillsModal] = useState(false);
  const [copingSkillToDelete, setCopingSkillToDelete] = useState(null);

  // Clinical Specialties state
  const [clinicalSpecialties, setClinicalSpecialties] = useState([]);
  const [clinicalSpecialtiesLoading, setClinicalSpecialtiesLoading] = useState(false);
  const [clinicalSpecialtiesError, setClinicalSpecialtiesError] = useState("");
  const [showClinicalSpecialtiesModal, setShowClinicalSpecialtiesModal] = useState(false);
  const [showEditClinicalSpecialtiesModal, setShowEditClinicalSpecialtiesModal] = useState(false);
  const [selectedClinicalSpecialty, setSelectedClinicalSpecialty] = useState(null);
  const [clinicalSpecialtiesForm, setClinicalSpecialtiesForm] = useState({
    short_description: "",
    long_description: ""
  });
  const [clinicalSpecialtiesFormLoading, setClinicalSpecialtiesFormLoading] = useState(false);
  const [clinicalSpecialtiesFormError, setClinicalSpecialtiesFormError] = useState("");
  const [showDeleteClinicalSpecialtiesModal, setShowDeleteClinicalSpecialtiesModal] = useState(false);
  const [clinicalSpecialtyToDelete, setClinicalSpecialtyToDelete] = useState(null);

  // Documentation Methods state
  const [documentationMethods, setDocumentationMethods] = useState([]);
  const [documentationMethodsLoading, setDocumentationMethodsLoading] = useState(false);
  const [documentationMethodsError, setDocumentationMethodsError] = useState("");
  const [showDocumentationMethodsModal, setShowDocumentationMethodsModal] = useState(false);
  const [showEditDocumentationMethodsModal, setShowEditDocumentationMethodsModal] = useState(false);
  const [selectedDocumentationMethod, setSelectedDocumentationMethod] = useState(null);
  const [documentationMethodsForm, setDocumentationMethodsForm] = useState({
    name: "",
    session_instructions: `[here you can enter some main instructions]

Section 1: Field "Methods"
[Enter your instructions for the Methods section here]

Section 2: Field "Progress_towards_goal" 
[Enter your instructions for the Progress towards goal section here]

Section 3: Field "Recommended_changes"
[Enter your instructions for the Recommended changes section here]

[Additional instructions and examples can be added here]`
  });
  const [documentationMethodsFormLoading, setDocumentationMethodsFormLoading] = useState(false);
  const [documentationMethodsFormError, setDocumentationMethodsFormError] = useState("");
  const [showDeleteDocumentationMethodsModal, setShowDeleteDocumentationMethodsModal] = useState(false);
  const [documentationMethodToDelete, setDocumentationMethodToDelete] = useState(null);
  const [showViewDocumentationMethodModal, setShowViewDocumentationMethodModal] = useState(false);
  const [selectedDocumentationMethodForView, setSelectedDocumentationMethodForView] = useState(null);

  // Modality state
  const [modalities, setModalities] = useState([]);
  const [modalitiesLoading, setModalitiesLoading] = useState(false);
  const [modalitiesError, setModalitiesError] = useState("");
  const [showModalityModal, setShowModalityModal] = useState(false);
  const [showEditModalityModal, setShowEditModalityModal] = useState(false);
  const [selectedModality, setSelectedModality] = useState(null);
  const [modalityForm, setModalityForm] = useState({
    name: "",
    short_term: "",
    description: "",
    modality_setting: ""
  });
  const [modalityFormLoading, setModalityFormLoading] = useState(false);
  const [modalityFormError, setModalityFormError] = useState("");
  const [showDeleteModalityModal, setShowDeleteModalityModal] = useState(false);
  const [modalityToDelete, setModalityToDelete] = useState(null);
  const [showViewModalityModal, setShowViewModalityModal] = useState(false);
  const [selectedModalityForView, setSelectedModalityForView] = useState(null);

  // Modality Steps state
  const [modalitySteps, setModalitySteps] = useState([]);
  const [modalityStepsLoading, setModalityStepsLoading] = useState(false);
  const [modalityStepsError, setModalityStepsError] = useState("");
  const [showModalityStepsModal, setShowModalityStepsModal] = useState(false);
  const [showEditModalityStepsModal, setShowEditModalityStepsModal] = useState(false);
  const [selectedModalityStep, setSelectedModalityStep] = useState(null);
  const [modalityStepsForm, setModalityStepsForm] = useState({
    modality_id: "",
    name: ""
  });
  const [modalityStepsFormLoading, setModalityStepsFormLoading] = useState(false);
  const [modalityStepsFormError, setModalityStepsFormError] = useState("");
  const [showDeleteModalityStepsModal, setShowDeleteModalityStepsModal] = useState(false);
  const [modalityStepToDelete, setModalityStepToDelete] = useState(null);

  // Activity state
  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [activitiesError, setActivitiesError] = useState("");
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showEditActivityModal, setShowEditActivityModal] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [activityForm, setActivityForm] = useState({
    name: "",
    short_term: "",
    description: "",
    activity_setting: ""
  });
  const [activityFormLoading, setActivityFormLoading] = useState(false);
  const [activityFormError, setActivityFormError] = useState("");
  const [showDeleteActivityModal, setShowDeleteActivityModal] = useState(false);
  const [activityToDelete, setActivityToDelete] = useState(null);
  const [showViewActivityModal, setShowViewActivityModal] = useState(false);
  const [selectedActivityForView, setSelectedActivityForView] = useState(null);

  // Sub-Activities state
  const [subActivities, setSubActivities] = useState([]);
  const [subActivitiesLoading, setSubActivitiesLoading] = useState(false);
  const [subActivitiesError, setSubActivitiesError] = useState("");
  const [showSubActivitiesModal, setShowSubActivitiesModal] = useState(false);
  const [showEditSubActivitiesModal, setShowEditSubActivitiesModal] = useState(false);
  const [selectedSubActivity, setSelectedSubActivity] = useState(null);
  const [subActivitiesForm, setSubActivitiesForm] = useState({
    activity_id: "",
    name: ""
  });
  const [subActivitiesFormLoading, setSubActivitiesFormLoading] = useState(false);
  const [subActivitiesFormError, setSubActivitiesFormError] = useState("");
  const [showDeleteSubActivitiesModal, setShowDeleteSubActivitiesModal] = useState(false);
  const [subActivityToDelete, setSubActivityToDelete] = useState(null);

  // Fetch functions
  const fetchCopingSkills = async () => {
    setCopingSkillsLoading(true);
    setCopingSkillsError("");
    try {
      const response = await fetch(`${environment.apiUrl}/coping-skills/`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setCopingSkills(data);
      } else {
        setCopingSkillsError("Failed to fetch coping skills");
      }
    } catch (err) {
      setCopingSkillsError("Network error");
    } finally {
      setCopingSkillsLoading(false);
    }
  };

  const fetchClinicalSpecialties = async () => {
    setClinicalSpecialtiesLoading(true);
    setClinicalSpecialtiesError("");
    try {
      const response = await fetch(`${environment.apiUrl}/clinical-specialties/`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setClinicalSpecialties(data);
      } else {
        setClinicalSpecialtiesError("Failed to fetch clinical specialties");
      }
    } catch (err) {
      setClinicalSpecialtiesError("Network error");
    } finally {
      setClinicalSpecialtiesLoading(false);
    }
  };

  const fetchDocumentationMethods = async () => {
    setDocumentationMethodsLoading(true);
    setDocumentationMethodsError("");
    try {
      const response = await fetch(`${environment.apiUrl}/documentation-methods/`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setDocumentationMethods(data);
      } else {
        setDocumentationMethodsError("Failed to fetch documentation methods");
      }
    } catch (err) {
      setDocumentationMethodsError("Network error");
    } finally {
      setDocumentationMethodsLoading(false);
    }
  };

  const fetchModalities = async () => {
    setModalitiesLoading(true);
    setModalitiesError("");
    try {
      const response = await fetch(`${environment.apiUrl}/modalities/`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setModalities(data);
      } else {
        setModalitiesError("Failed to fetch modalities");
      }
    } catch (err) {
      setModalitiesError("Network error");
    } finally {
      setModalitiesLoading(false);
    }
  };

  const fetchModalitySteps = async () => {
    setModalityStepsLoading(true);
    setModalityStepsError("");
    try {
      const response = await fetch(`${environment.apiUrl}/modality-steps/`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setModalitySteps(data);
      } else {
        setModalityStepsError("Failed to fetch modality steps");
      }
    } catch (err) {
      setModalityStepsError("Network error");
    } finally {
      setModalityStepsLoading(false);
    }
  };

  const fetchActivities = async () => {
    setActivitiesLoading(true);
    setActivitiesError("");
    try {
      const response = await fetch(`${environment.apiUrl}/activities/`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setActivities(data);
      } else {
        setActivitiesError("Failed to fetch activities");
      }
    } catch (err) {
      setActivitiesError("Network error");
    } finally {
      setActivitiesLoading(false);
    }
  };

  const fetchSubActivities = async () => {
    setSubActivitiesLoading(true);
    setSubActivitiesError("");
    try {
      const response = await fetch(`${environment.apiUrl}/sub-activities/`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setSubActivities(data);
      } else {
        setSubActivitiesError("Failed to fetch sub-activities");
      }
    } catch (err) {
      setSubActivitiesError("Network error");
    } finally {
      setSubActivitiesLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "coping-skills") {
      fetchCopingSkills();
    } else if (activeTab === "clinical-specialties") {
      fetchClinicalSpecialties();
    } else if (activeTab === "documentation-methods") {
      fetchDocumentationMethods();
    } else if (activeTab === "modality") {
      fetchModalities();
    } else if (activeTab === "modality-steps") {
      fetchModalitySteps();
      fetchModalities(); // Also fetch modalities for the lookup
    } else if (activeTab === "activity") {
      fetchActivities();
    } else if (activeTab === "sub-activities") {
      fetchSubActivities();
      fetchActivities(); // Also fetch activities for the lookup
    }
  }, [activeTab]);

  // Coping Skills CRUD operations
  const handleCreateCopingSkill = async (e) => {
    e.preventDefault();
    setCopingSkillsFormLoading(true);
    setCopingSkillsFormError("");

    try {
      const response = await fetch(`${environment.apiUrl}/coping-skills/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(copingSkillsForm),
      });

      if (response.ok) {
        setShowCopingSkillsModal(false);
        setCopingSkillsForm({ short_description: "", long_description: "" });
        fetchCopingSkills();
      } else {
        const data = await response.json();
        setCopingSkillsFormError(data.detail || "Failed to create coping skill");
      }
    } catch (err) {
      setCopingSkillsFormError("Network error");
    } finally {
      setCopingSkillsFormLoading(false);
    }
  };

  const handleUpdateCopingSkill = async (e) => {
    e.preventDefault();
    setCopingSkillsFormLoading(true);
    setCopingSkillsFormError("");

    try {
      const response = await fetch(`${environment.apiUrl}/coping-skills/${selectedCopingSkill.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(copingSkillsForm),
      });

      if (response.ok) {
        setShowEditCopingSkillsModal(false);
        setSelectedCopingSkill(null);
        setCopingSkillsForm({ short_description: "", long_description: "" });
        fetchCopingSkills();
      } else {
        const data = await response.json();
        setCopingSkillsFormError(data.detail || "Failed to update coping skill");
      }
    } catch (err) {
      setCopingSkillsFormError("Network error");
    } finally {
      setCopingSkillsFormLoading(false);
    }
  };

  const handleDeleteCopingSkill = async () => {
    if (!copingSkillToDelete) return;
    try {
      const response = await fetch(`${environment.apiUrl}/coping-skills/${copingSkillToDelete.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (response.ok) {
        fetchCopingSkills();
      } else {
        setCopingSkillsError("Failed to delete coping skill");
      }
    } catch (err) {
      setCopingSkillsError("Network error");
    } finally {
      setShowDeleteCopingSkillsModal(false);
      setCopingSkillToDelete(null);
    }
  };

  const openEditCopingSkillModal = (copingSkill) => {
    setSelectedCopingSkill(copingSkill);
    setCopingSkillsForm({
      short_description: copingSkill.short_description,
      long_description: copingSkill.long_description
    });
    setShowEditCopingSkillsModal(true);
  };

  // Clinical Specialties CRUD operations
  const handleCreateClinicalSpecialty = async (e) => {
    e.preventDefault();
    setClinicalSpecialtiesFormLoading(true);
    setClinicalSpecialtiesFormError("");

    try {
      const response = await fetch(`${environment.apiUrl}/clinical-specialties/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(clinicalSpecialtiesForm),
      });

      if (response.ok) {
        setShowClinicalSpecialtiesModal(false);
        setClinicalSpecialtiesForm({ short_description: "", long_description: "" });
        fetchClinicalSpecialties();
      } else {
        const data = await response.json();
        setClinicalSpecialtiesFormError(data.detail || "Failed to create clinical specialty");
      }
    } catch (err) {
      setClinicalSpecialtiesFormError("Network error");
    } finally {
      setClinicalSpecialtiesFormLoading(false);
    }
  };

  const handleUpdateClinicalSpecialty = async (e) => {
    e.preventDefault();
    setClinicalSpecialtiesFormLoading(true);
    setClinicalSpecialtiesFormError("");

    try {
      const response = await fetch(`${environment.apiUrl}/clinical-specialties/${selectedClinicalSpecialty.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(clinicalSpecialtiesForm),
      });

      if (response.ok) {
        setShowEditClinicalSpecialtiesModal(false);
        setSelectedClinicalSpecialty(null);
        setClinicalSpecialtiesForm({ short_description: "", long_description: "" });
        fetchClinicalSpecialties();
      } else {
        const data = await response.json();
        setClinicalSpecialtiesFormError(data.detail || "Failed to update clinical specialty");
      }
    } catch (err) {
      setClinicalSpecialtiesFormError("Network error");
    } finally {
      setClinicalSpecialtiesFormLoading(false);
    }
  };

  const handleDeleteClinicalSpecialty = async () => {
    if (!clinicalSpecialtyToDelete) return;
    try {
      const response = await fetch(`${environment.apiUrl}/clinical-specialties/${clinicalSpecialtyToDelete.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (response.ok) {
        fetchClinicalSpecialties();
      } else {
        setClinicalSpecialtiesError("Failed to delete clinical specialty");
      }
    } catch (err) {
      setClinicalSpecialtiesError("Network error");
    } finally {
      setShowDeleteClinicalSpecialtiesModal(false);
      setClinicalSpecialtyToDelete(null);
    }
  };

  const openEditClinicalSpecialtyModal = (clinicalSpecialty) => {
    setSelectedClinicalSpecialty(clinicalSpecialty);
    setClinicalSpecialtiesForm({
      short_description: clinicalSpecialty.short_description,
      long_description: clinicalSpecialty.long_description
    });
    setShowEditClinicalSpecialtiesModal(true);
  };

  // Documentation Methods CRUD operations
  const handleCreateDocumentationMethod = async (e) => {
    e.preventDefault();
    setDocumentationMethodsFormLoading(true);
    setDocumentationMethodsFormError("");

    try {
      const response = await fetch(`${environment.apiUrl}/documentation-methods/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(documentationMethodsForm),
      });

      if (response.ok) {
        setShowDocumentationMethodsModal(false);
        setDocumentationMethodsForm({ 
          name: "", 
          session_instructions: `[here you can enter some main instructions]

Section 1: Field "Methods"
[Enter your instructions for the Methods section here]

Section 2: Field "Progress_towards_goal" 
[Enter your instructions for the Progress towards goal section here]

Section 3: Field "Recommended_changes"
[Enter your instructions for the Recommended changes section here]

[Additional instructions and examples can be added here]`
        });
        fetchDocumentationMethods();
      } else {
        const data = await response.json();
        setDocumentationMethodsFormError(data.detail || "Failed to create documentation method");
      }
    } catch (err) {
      setDocumentationMethodsFormError("Network error");
    } finally {
      setDocumentationMethodsFormLoading(false);
    }
  };

  const handleUpdateDocumentationMethod = async (e) => {
    e.preventDefault();
    setDocumentationMethodsFormLoading(true);
    setDocumentationMethodsFormError("");

    try {
      const response = await fetch(`${environment.apiUrl}/documentation-methods/${selectedDocumentationMethod.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(documentationMethodsForm),
      });

      if (response.ok) {
        setShowEditDocumentationMethodsModal(false);
        setSelectedDocumentationMethod(null);
        setDocumentationMethodsForm({ 
          name: "", 
          session_instructions: `[here you can enter some main instructions]

Section 1: Field "Methods"
[Enter your instructions for the Methods section here]

Section 2: Field "Progress_towards_goal" 
[Enter your instructions for the Progress towards goal section here]

Section 3: Field "Recommended_changes"
[Enter your instructions for the Recommended changes section here]

[Additional instructions and examples can be added here]`
        });
        fetchDocumentationMethods();
      } else {
        const data = await response.json();
        setDocumentationMethodsFormError(data.detail || "Failed to update documentation method");
      }
    } catch (err) {
      setDocumentationMethodsFormError("Network error");
    } finally {
      setDocumentationMethodsFormLoading(false);
    }
  };

  const handleDeleteDocumentationMethod = async () => {
    if (!documentationMethodToDelete) return;
    try {
      const response = await fetch(`${environment.apiUrl}/documentation-methods/${documentationMethodToDelete.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (response.ok) {
        fetchDocumentationMethods();
      } else {
        setDocumentationMethodsError("Failed to delete documentation method");
      }
    } catch (err) {
      setDocumentationMethodsError("Network error");
    } finally {
      setShowDeleteDocumentationMethodsModal(false);
      setDocumentationMethodToDelete(null);
    }
  };

  const openEditDocumentationMethodModal = (documentationMethod) => {
    setSelectedDocumentationMethod(documentationMethod);
    setDocumentationMethodsForm({
      name: documentationMethod.name,
      session_instructions: documentationMethod.session_instructions
    });
    setShowEditDocumentationMethodsModal(true);
  };

  const openViewDocumentationMethodModal = async (documentationMethod) => {
    try {
      // Fetch the full documentation method details from the backend
      const response = await fetch(`${environment.apiUrl}/documentation-methods/${documentationMethod.id}`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      if (response.ok) {
        const fullDocumentationMethod = await response.json();
        setSelectedDocumentationMethodForView(fullDocumentationMethod);
        setShowViewDocumentationMethodModal(true);
      } else {
        // Fallback to using the data from the table if API call fails
        setSelectedDocumentationMethodForView(documentationMethod);
        setShowViewDocumentationMethodModal(true);
      }
    } catch (err) {
      // Fallback to using the data from the table if network error
      setSelectedDocumentationMethodForView(documentationMethod);
      setShowViewDocumentationMethodModal(true);
    }
  };

  // Modality CRUD operations
  const handleCreateModality = async (e) => {
    e.preventDefault();
    setModalityFormLoading(true);
    setModalityFormError("");

    try {
      const response = await fetch(`${environment.apiUrl}/modalities/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(modalityForm),
      });

      if (response.ok) {
        setShowModalityModal(false);
        setModalityForm({ name: "", short_term: "", description: "", modality_setting: "" });
        fetchModalities();
      } else {
        const data = await response.json();
        setModalityFormError(data.detail || "Failed to create modality");
      }
    } catch (err) {
      setModalityFormError("Network error");
    } finally {
      setModalityFormLoading(false);
    }
  };

  const handleUpdateModality = async (e) => {
    e.preventDefault();
    setModalityFormLoading(true);
    setModalityFormError("");

    try {
      const response = await fetch(`${environment.apiUrl}/modalities/${selectedModality.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(modalityForm),
      });

      if (response.ok) {
        setShowEditModalityModal(false);
        setSelectedModality(null);
        setModalityForm({ name: "", short_term: "", description: "", modality_setting: "" });
        fetchModalities();
      } else {
        const data = await response.json();
        setModalityFormError(data.detail || "Failed to update modality");
      }
    } catch (err) {
      setModalityFormError("Network error");
    } finally {
      setModalityFormLoading(false);
    }
  };

  const handleDeleteModality = async () => {
    if (!modalityToDelete) return;
    try {
      const response = await fetch(`${environment.apiUrl}/modalities/${modalityToDelete.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (response.ok) {
        fetchModalities();
      } else {
        setModalitiesError("Failed to delete modality");
      }
    } catch (err) {
      setModalitiesError("Network error");
    } finally {
      setShowDeleteModalityModal(false);
      setModalityToDelete(null);
    }
  };

  const openEditModalityModal = (modality) => {
    setSelectedModality(modality);
    setModalityForm({
      name: modality.name,
      short_term: modality.short_term,
      description: modality.description,
      modality_setting: modality.modality_setting
    });
    setShowEditModalityModal(true);
  };

  const openViewModalityModal = (modality) => {
    navigate(`/modality/${modality.id}`);
  };

  // Modality Steps CRUD operations
  const handleCreateModalitySteps = async (e) => {
    e.preventDefault();
    setModalityStepsFormLoading(true);
    setModalityStepsFormError("");

    try {
      // Split the name by newlines to create multiple steps
      const steps = modalityStepsForm.name.split('\n').filter(step => step.trim() !== '');
      
      // Create each step
      for (const stepName of steps) {
        const response = await fetch(`${environment.apiUrl}/modality-steps/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify({
            modality_id: modalityStepsForm.modality_id,
            name: stepName.trim()
          }),
        });

        if (!response.ok) {
          const data = await response.json();
          setModalityStepsFormError(data.detail || "Failed to create modality step");
          return;
        }
      }

      setShowModalityStepsModal(false);
      setModalityStepsForm({ modality_id: "", name: "" });
      fetchModalitySteps();
    } catch (err) {
      setModalityStepsFormError("Network error");
    } finally {
      setModalityStepsFormLoading(false);
    }
  };

  const handleUpdateModalitySteps = async (e) => {
    e.preventDefault();
    setModalityStepsFormLoading(true);
    setModalityStepsFormError("");

    try {
      const response = await fetch(`${environment.apiUrl}/modality-steps/${selectedModalityStep.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          modality_id: modalityStepsForm.modality_id,
          name: modalityStepsForm.name
        }),
      });

      if (response.ok) {
        setShowEditModalityStepsModal(false);
        setSelectedModalityStep(null);
        setModalityStepsForm({ modality_id: "", name: "" });
        fetchModalitySteps();
      } else {
        const data = await response.json();
        setModalityStepsFormError(data.detail || "Failed to update modality step");
      }
    } catch (err) {
      setModalityStepsFormError("Network error");
    } finally {
      setModalityStepsFormLoading(false);
    }
  };

  const handleDeleteModalitySteps = async () => {
    if (!modalityStepToDelete) return;
    try {
      const response = await fetch(`${environment.apiUrl}/modality-steps/${modalityStepToDelete.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (response.ok) {
        fetchModalitySteps();
      } else {
        setModalityStepsError("Failed to delete modality step");
      }
    } catch (err) {
      setModalityStepsError("Network error");
    } finally {
      setShowDeleteModalityStepsModal(false);
      setModalityStepToDelete(null);
    }
  };

  const openEditModalityStepsModal = (modalityStep) => {
    setSelectedModalityStep(modalityStep);
    setModalityStepsForm({
      modality_id: modalityStep.modality_id,
      name: modalityStep.name
    });
    setShowEditModalityStepsModal(true);
  };

  // Activity CRUD operations
  const handleCreateActivity = async (e) => {
    e.preventDefault();
    setActivityFormLoading(true);
    setActivityFormError("");

    try {
      const response = await fetch(`${environment.apiUrl}/activities/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(activityForm),
      });

      if (response.ok) {
        setShowActivityModal(false);
        setActivityForm({ name: "", short_term: "", description: "", activity_setting: "" });
        fetchActivities();
      } else {
        const data = await response.json();
        setActivityFormError(data.detail || "Failed to create activity");
      }
    } catch (err) {
      setActivityFormError("Network error");
    } finally {
      setActivityFormLoading(false);
    }
  };

  const handleUpdateActivity = async (e) => {
    e.preventDefault();
    setActivityFormLoading(true);
    setActivityFormError("");

    try {
      const response = await fetch(`${environment.apiUrl}/activities/${selectedActivity.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(activityForm),
      });

      if (response.ok) {
        setShowEditActivityModal(false);
        setSelectedActivity(null);
        setActivityForm({ name: "", short_term: "", description: "", activity_setting: "" });
        fetchActivities();
      } else {
        const data = await response.json();
        setActivityFormError(data.detail || "Failed to update activity");
      }
    } catch (err) {
      setActivityFormError("Network error");
    } finally {
      setActivityFormLoading(false);
    }
  };

  const handleDeleteActivity = async () => {
    if (!activityToDelete) return;
    try {
      const response = await fetch(`${environment.apiUrl}/activities/${activityToDelete.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (response.ok) {
        fetchActivities();
      } else {
        setActivitiesError("Failed to delete activity");
      }
    } catch (err) {
      setActivitiesError("Network error");
    } finally {
      setShowDeleteActivityModal(false);
      setActivityToDelete(null);
    }
  };

  const openEditActivityModal = (activity) => {
    setSelectedActivity(activity);
    setActivityForm({
      name: activity.name,
      short_term: activity.short_term,
      description: activity.description,
      activity_setting: activity.activity_setting
    });
    setShowEditActivityModal(true);
  };

  const openViewActivityModal = (activity) => {
    navigate(`/activity/${activity.id}`);
  };

  // Sub-Activities CRUD operations
  const handleCreateSubActivities = async (e) => {
    e.preventDefault();
    setSubActivitiesFormLoading(true);
    setSubActivitiesFormError("");

    try {
      const steps = subActivitiesForm.name.split('\n').filter(step => step.trim() !== '');
      
      for (const stepName of steps) {
        const response = await fetch(`${environment.apiUrl}/sub-activities/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify({
            activity_id: subActivitiesForm.activity_id,
            name: stepName.trim()
          }),
        });

        if (!response.ok) {
          const data = await response.json();
          setSubActivitiesFormError(data.detail || "Failed to create sub-activity");
          return;
        }
      }

      setShowSubActivitiesModal(false);
      setSubActivitiesForm({ activity_id: "", name: "" });
      fetchSubActivities();
    } catch (err) {
      setSubActivitiesFormError("Network error");
    } finally {
      setSubActivitiesFormLoading(false);
    }
  };

  const handleUpdateSubActivities = async (e) => {
    e.preventDefault();
    setSubActivitiesFormLoading(true);
    setSubActivitiesFormError("");

    try {
      const response = await fetch(`${environment.apiUrl}/sub-activities/${selectedSubActivity.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          activity_id: subActivitiesForm.activity_id,
          name: subActivitiesForm.name
        }),
      });

      if (response.ok) {
        setShowEditSubActivitiesModal(false);
        setSelectedSubActivity(null);
        setSubActivitiesForm({ activity_id: "", name: "" });
        fetchSubActivities();
      } else {
        const data = await response.json();
        setSubActivitiesFormError(data.detail || "Failed to update sub-activity");
      }
    } catch (err) {
      setSubActivitiesFormError("Network error");
    } finally {
      setSubActivitiesFormLoading(false);
    }
  };

  const handleDeleteSubActivities = async () => {
    if (!subActivityToDelete) return;
    try {
      const response = await fetch(`${environment.apiUrl}/sub-activities/${subActivityToDelete.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (response.ok) {
        fetchSubActivities();
      } else {
        setSubActivitiesError("Failed to delete sub-activity");
      }
    } catch (err) {
      setSubActivitiesError("Network error");
    } finally {
      setShowDeleteSubActivitiesModal(false);
      setSubActivityToDelete(null);
    }
  };

  const openEditSubActivitiesModal = (subActivity) => {
    setSelectedSubActivity(subActivity);
    setSubActivitiesForm({
      activity_id: subActivity.activity_id,
      name: subActivity.name
    });
    setShowEditSubActivitiesModal(true);
  };

  return (
    <div className="w-full">
      {/* Header with Tab Navigation */}
      <div className="flex justify-between items-center mb-8">
        <div className="flex bg-slate-100 rounded-xl p-1 shadow-sm flex-wrap gap-1">
          <button
            className={`px-5 py-2.5 font-semibold text-sm rounded-lg transition-all duration-200 whitespace-nowrap ${
              activeTab === "coping-skills"
                ? "bg-white text-blue-600 shadow-md"
                : "text-slate-600 hover:text-slate-800 hover:bg-slate-50"
            }`}
            onClick={() => setActiveTab("coping-skills")}
          >
            Coping Skills
          </button>
          <button
            className={`px-5 py-2.5 font-semibold text-sm rounded-lg transition-all duration-200 whitespace-nowrap ${
              activeTab === "clinical-specialties"
                ? "bg-white text-blue-600 shadow-md"
                : "text-slate-600 hover:text-slate-800 hover:bg-slate-50"
            }`}
            onClick={() => setActiveTab("clinical-specialties")}
          >
            Clinical Specialties
          </button>
          <button
            className={`px-5 py-2.5 font-semibold text-sm rounded-lg transition-all duration-200 whitespace-nowrap ${
              activeTab === "documentation-methods"
                ? "bg-white text-blue-600 shadow-md"
                : "text-slate-600 hover:text-slate-800 hover:bg-slate-50"
            }`}
            onClick={() => setActiveTab("documentation-methods")}
          >
            Documentation Methods
          </button>
          {isAdminOrSuperAdmin && (
            <>
              <button
                className={`px-5 py-2.5 font-semibold text-sm rounded-lg transition-all duration-200 whitespace-nowrap ${
                  activeTab === "modality"
                    ? "bg-white text-blue-600 shadow-md"
                    : "text-slate-600 hover:text-slate-800 hover:bg-slate-50"
                }`}
                onClick={() => setActiveTab("modality")}
              >
                Modalities
              </button>
              <button
                className={`px-5 py-2.5 font-semibold text-sm rounded-lg transition-all duration-200 whitespace-nowrap ${
                  activeTab === "modality-steps"
                    ? "bg-white text-blue-600 shadow-md"
                    : "text-slate-600 hover:text-slate-800 hover:bg-slate-50"
                }`}
                onClick={() => setActiveTab("modality-steps")}
              >
                Modality Steps
              </button>
              <button
                className={`px-5 py-2.5 font-semibold text-sm rounded-lg transition-all duration-200 whitespace-nowrap ${
                  activeTab === "activity"
                    ? "bg-white text-blue-600 shadow-md"
                    : "text-slate-600 hover:text-slate-800 hover:bg-slate-50"
                }`}
                onClick={() => setActiveTab("activity")}
              >
                Activities
              </button>
              <button
                className={`px-5 py-2.5 font-semibold text-sm rounded-lg transition-all duration-200 whitespace-nowrap ${
                  activeTab === "sub-activities"
                    ? "bg-white text-blue-600 shadow-md"
                    : "text-slate-600 hover:text-slate-800 hover:bg-slate-50"
                }`}
                onClick={() => setActiveTab("sub-activities")}
              >
                Sub-Activities
              </button>
            </>
          )}
        </div>
        {activeTab === "coping-skills" && (
          <button
            onClick={() => setShowCopingSkillsModal(true)}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-semibold"
          >
            <PlusIcon className="w-5 h-5" />
            Add Coping Skill
          </button>
        )}
        {activeTab === "clinical-specialties" && (
          <button
            onClick={() => setShowClinicalSpecialtiesModal(true)}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-semibold"
          >
            <PlusIcon className="w-5 h-5" />
            Add Clinical Specialty
          </button>
        )}
        {activeTab === "documentation-methods" && (
          <button
            onClick={() => setShowDocumentationMethodsModal(true)}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-semibold"
          >
            <PlusIcon className="w-5 h-5" />
            Add Documentation Method
          </button>
        )}
        {isAdminOrSuperAdmin && activeTab === "modality" && (
          <button
            onClick={() => {
              setSelectedModality(null);
              setModalityForm({ name: "", short_term: "", description: "", modality_setting: "" });
              setShowModalityModal(true);
            }}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-semibold"
          >
            <PlusIcon className="w-5 h-5" />
            Add Modality
          </button>
        )}
        {isAdminOrSuperAdmin && activeTab === "modality-steps" && (
          <button
            onClick={() => {
              setSelectedModalityStep(null);
              setModalityStepsForm({ modality_id: "", name: "" });
              setShowModalityStepsModal(true);
            }}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-semibold"
          >
            <PlusIcon className="w-5 h-5" />
            Add Modality Steps
          </button>
        )}
        {isAdminOrSuperAdmin && activeTab === "activity" && (
          <button
            onClick={() => {
              setSelectedActivity(null);
              setActivityForm({ name: "", short_term: "", description: "", activity_setting: "" });
              setShowActivityModal(true);
            }}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-semibold"
          >
            <PlusIcon className="w-5 h-5" />
            Add Activity
          </button>
        )}
        {isAdminOrSuperAdmin && activeTab === "sub-activities" && (
          <button
            onClick={() => {
              setSelectedSubActivity(null);
              setSubActivitiesForm({ activity_id: "", name: "" });
              setShowSubActivitiesModal(true);
            }}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-semibold"
          >
            <PlusIcon className="w-5 h-5" />
            Add Sub-Activities
          </button>
        )}
      </div>

      {/* Coping Skills Tab */}
      {activeTab === "coping-skills" && (
        <div className="w-full">
          {copingSkillsError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 shadow-sm">
              {copingSkillsError}
            </div>
          )}

          {copingSkillsLoading ? (
            <div className="text-center py-12 text-slate-500">
              <div className="flex items-center justify-center gap-3">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span className="text-lg">Loading coping skills...</span>
              </div>
            </div>
          ) : (
            <div className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 rounded-xl shadow-lg p-6 border border-slate-200">
              {copingSkills.length === 0 ? (
                <div className="p-12 text-center text-slate-500">
                  <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-10 h-10 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                  <p className="text-xl font-semibold text-slate-900 mb-2">No coping skills found</p>
                  <p className="text-slate-600">Create your first coping skill to get started</p>
                </div>
              ) : (
                <div className="rounded-xl">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gradient-to-r from-blue-50 to-indigo-50 text-slate-700 border-b border-slate-200">
                        <th className="px-4 py-4 text-left font-semibold text-slate-800 w-1/4">
                          Short Description
                        </th>
                        <th className="px-4 py-4 text-left font-semibold text-slate-800 w-1/2">
                          Long Description
                        </th>
                        <th className="px-4 py-4 text-left font-semibold text-slate-800 w-1/6">
                          Created
                        </th>
                        <th className="px-4 py-4 text-right font-semibold text-slate-800 w-20">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {copingSkills.map((skill, index) => (
                        <tr
                          key={skill.id}
                          className={`border-b border-slate-100 transition-all duration-200 ${
                            index % 2 === 0 ? "bg-white" : "bg-slate-50"
                          } hover:bg-blue-50 hover:shadow-sm`}
                        >
                          <td className="px-4 py-4 font-medium text-slate-900">
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                                {skill.short_description ? skill.short_description[0].toUpperCase() : "C"}
                              </div>
                              <span className="font-semibold truncate">{skill.short_description}</span>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-slate-700 max-w-xs">
                            <div className="text-sm break-words overflow-hidden">
                              {skill.long_description 
                                ? (skill.long_description.length > 50 
                                    ? skill.long_description.substring(0, 50) + "..." 
                                    : skill.long_description)
                                : "—"}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-slate-500">
                            <div className="text-sm truncate">
                              {new Date(skill.created_at).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              })}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-right">
                            <div className="flex justify-end gap-0.5">
                              <button
                                onClick={() => openEditCopingSkillModal(skill)}
                                className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-all duration-200 hover:shadow-sm"
                                title="Edit coping skill"
                              >
                                <PencilIcon className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => { setShowDeleteCopingSkillsModal(true); setCopingSkillToDelete(skill); }}
                                className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all duration-200 hover:shadow-sm"
                                title="Delete coping skill"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Clinical Specialties Tab */}
      {activeTab === "clinical-specialties" && (
        <div className="w-full">
          {clinicalSpecialtiesError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 shadow-sm">
              {clinicalSpecialtiesError}
            </div>
          )}

          {clinicalSpecialtiesLoading ? (
            <div className="text-center py-12 text-slate-500">
              <div className="flex items-center justify-center gap-3">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span className="text-lg">Loading clinical specialties...</span>
              </div>
            </div>
          ) : (
            <div className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 rounded-xl shadow-lg p-6 border border-slate-200">
              {clinicalSpecialties.length === 0 ? (
                <div className="p-12 text-center text-slate-500">
                  <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-10 h-10 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                    </svg>
                  </div>
                  <p className="text-xl font-semibold text-slate-900 mb-2">No clinical specialties found</p>
                  <p className="text-slate-600">Create your first clinical specialty to get started</p>
                </div>
              ) : (
                <div className="rounded-xl">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gradient-to-r from-blue-50 to-indigo-50 text-slate-700 border-b border-slate-200">
                        <th className="px-4 py-4 text-left font-semibold text-slate-800 w-1/4">
                          Short Description
                        </th>
                        <th className="px-4 py-4 text-left font-semibold text-slate-800 w-1/2">
                          Long Description
                        </th>
                        <th className="px-4 py-4 text-left font-semibold text-slate-800 w-1/6">
                          Created
                        </th>
                        <th className="px-4 py-4 text-right font-semibold text-slate-800 w-20">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {clinicalSpecialties.map((specialty, index) => (
                        <tr
                          key={specialty.id}
                          className={`border-b border-slate-100 transition-all duration-200 ${
                            index % 2 === 0 ? "bg-white" : "bg-slate-50"
                          } hover:bg-blue-50 hover:shadow-sm`}
                        >
                          <td className="px-4 py-4 font-medium text-slate-900">
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                                {specialty.short_description ? specialty.short_description[0].toUpperCase() : "S"}
                              </div>
                              <span className="font-semibold truncate">{specialty.short_description}</span>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-slate-700 max-w-xs">
                            <div className="text-sm break-words overflow-hidden">
                              {specialty.long_description 
                                ? (specialty.long_description.length > 50 
                                    ? specialty.long_description.substring(0, 50) + "..." 
                                    : specialty.long_description)
                                : "—"}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-slate-500">
                            <div className="text-sm truncate">
                              {new Date(specialty.created_at).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              })}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-right">
                            <div className="flex justify-end gap-0.5">
                              <button
                                onClick={() => openEditClinicalSpecialtyModal(specialty)}
                                className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-all duration-200 hover:shadow-sm"
                                title="Edit clinical specialty"
                              >
                                <PencilIcon className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => { setShowDeleteClinicalSpecialtiesModal(true); setClinicalSpecialtyToDelete(specialty); }}
                                className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all duration-200 hover:shadow-sm"
                                title="Delete clinical specialty"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Modality Tab */}
      {activeTab === "modality" && (
        <div className="w-full">
          {modalitiesError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 shadow-sm">
              {modalitiesError}
            </div>
          )}

          {modalitiesLoading ? (
            <div className="text-center py-12 text-slate-500">
              <div className="flex items-center justify-center gap-3">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span className="text-lg">Loading modalities...</span>
              </div>
            </div>
          ) : (
            <div className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 rounded-xl shadow-lg p-6 border border-slate-200">
              {modalities.length === 0 ? (
                <div className="p-12 text-center text-slate-500">
                  <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-10 h-10 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </div>
                  <p className="text-xl font-semibold text-slate-900 mb-2">No modalities found</p>
                  <p className="text-slate-600">Create your first modality to get started</p>
                </div>
              ) : (
                <div className="rounded-xl">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gradient-to-r from-blue-50 to-indigo-50 text-slate-700 border-b border-slate-200">
                        <th className="px-4 py-4 text-left font-semibold text-slate-800 w-1/6">
                          Name
                        </th>
                        <th className="px-4 py-4 text-left font-semibold text-slate-800 w-1/6">
                          Short Term
                        </th>
                        <th className="px-4 py-4 text-left font-semibold text-slate-800 w-1/3">
                          Description
                        </th>
                        <th className="px-4 py-4 text-left font-semibold text-slate-800 w-1/6">
                          Modality Setting
                        </th>
                        <th className="px-4 py-4 text-left font-semibold text-slate-800 w-1/8">
                          Created
                        </th>
                        <th className="px-4 py-4 text-right font-semibold text-slate-800 w-20">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {modalities.map((modality, index) => (
                        <tr
                          key={modality.id}
                          className={`border-b border-slate-100 transition-all duration-200 ${
                            index % 2 === 0 ? "bg-white" : "bg-slate-50"
                          } hover:bg-blue-50 hover:shadow-sm cursor-pointer`}
                          onClick={() => openViewModalityModal(modality)}
                        >
                          <td className="px-4 py-4 font-medium text-slate-900">
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                                {modality.name ? modality.name[0].toUpperCase() : "M"}
                              </div>
                              <span className="font-semibold truncate">{modality.name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-slate-700">
                            <div className="text-sm truncate">{modality.short_term || "—"}</div>
                          </td>
                          <td className="px-4 py-4 text-slate-700 max-w-xs">
                            <div className="text-sm break-words overflow-hidden">
                              {modality.description 
                                ? (modality.description.length > 50 
                                    ? modality.description.substring(0, 50) + "..." 
                                    : modality.description)
                                : "—"}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-slate-700">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              {modality.modality_setting || "—"}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-slate-500">
                            <div className="text-sm truncate">
                              {new Date(modality.created_at).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              })}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-right">
                            <div className="flex justify-end gap-0.5">
                              <button
                                onClick={(e) => { e.stopPropagation(); openEditModalityModal(modality); }}
                                className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-all duration-200 hover:shadow-sm"
                                title="Edit modality"
                              >
                                <PencilIcon className="w-4 h-4" />
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); setShowDeleteModalityModal(true); setModalityToDelete(modality); }}
                                className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all duration-200 hover:shadow-sm"
                                title="Delete modality"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Activity Tab */}
      {activeTab === "activity" && (
        <div className="w-full">
          {activitiesError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 shadow-sm">
              {activitiesError}
            </div>
          )}

          {activitiesLoading ? (
            <div className="text-center py-12 text-slate-500">
              <div className="flex items-center justify-center gap-3">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span className="text-lg">Loading activities...</span>
              </div>
            </div>
          ) : (
            <div className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 rounded-xl shadow-lg p-6 border border-slate-200">
              {activities.length === 0 ? (
                <div className="p-12 text-center text-slate-500">
                  <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-10 h-10 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <p className="text-xl font-semibold text-slate-900 mb-2">No activities found</p>
                  <p className="text-slate-600">Create your first activity to get started</p>
                </div>
              ) : (
                <div className="rounded-xl">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gradient-to-r from-blue-50 to-indigo-50 text-slate-700 border-b border-slate-200">
                        <th className="px-4 py-4 text-left font-semibold text-slate-800 w-1/6">
                          Name
                        </th>
                        <th className="px-4 py-4 text-left font-semibold text-slate-800 w-1/6">
                          Short Term
                        </th>
                        <th className="px-4 py-4 text-left font-semibold text-slate-800 w-1/3">
                          Description
                        </th>
                        <th className="px-4 py-4 text-left font-semibold text-slate-800 w-1/6">
                          Activity Setting
                        </th>
                        <th className="px-4 py-4 text-left font-semibold text-slate-800 w-1/8">
                          Created
                        </th>
                        <th className="px-4 py-4 text-right font-semibold text-slate-800 w-20">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {activities.map((activity, index) => (
                        <tr
                          key={activity.id}
                          className={`border-b border-slate-100 transition-all duration-200 ${
                            index % 2 === 0 ? "bg-white" : "bg-slate-50"
                          } hover:bg-blue-50 hover:shadow-sm cursor-pointer`}
                          onClick={() => openViewActivityModal(activity)}
                        >
                          <td className="px-4 py-4 font-medium text-slate-900">
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-pink-500 to-pink-600 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                                {activity.name ? activity.name[0].toUpperCase() : "A"}
                              </div>
                              <span className="font-semibold truncate">{activity.name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-slate-700">
                            <div className="text-sm truncate">{activity.short_term || "—"}</div>
                          </td>
                          <td className="px-4 py-4 text-slate-700 max-w-xs">
                            <div className="text-sm break-words overflow-hidden">
                              {activity.description 
                                ? (activity.description.length > 50 
                                    ? activity.description.substring(0, 50) + "..." 
                                    : activity.description)
                                : "—"}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-slate-700">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-pink-100 text-pink-800">
                              {activity.activity_setting || "—"}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-slate-500">
                            <div className="text-sm truncate">
                              {new Date(activity.created_at).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              })}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-right">
                            <div className="flex justify-end gap-0.5">
                              <button
                                onClick={(e) => { e.stopPropagation(); openEditActivityModal(activity); }}
                                className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-all duration-200 hover:shadow-sm"
                                title="Edit activity"
                              >
                                <PencilIcon className="w-4 h-4" />
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); setShowDeleteActivityModal(true); setActivityToDelete(activity); }}
                                className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all duration-200 hover:shadow-sm"
                                title="Delete activity"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Sub-Activities Tab */}
      {isAdminOrSuperAdmin && activeTab === "sub-activities" && (
        <div className="w-full">
          {subActivitiesError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 shadow-sm">
              {subActivitiesError}
            </div>
          )}

          {subActivitiesLoading ? (
            <div className="text-center py-12 text-slate-500">
              <div className="flex items-center justify-center gap-3">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span className="text-lg">Loading sub-activities...</span>
              </div>
            </div>
          ) : (
            <div className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 rounded-xl shadow-lg p-6 border border-slate-200">
              {subActivities.length === 0 ? (
                <div className="p-12 text-center text-slate-500">
                  <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-10 h-10 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                    </svg>
                  </div>
                  <p className="text-xl font-semibold text-slate-900 mb-2">No sub-activities found</p>
                  <p className="text-slate-600">Create your first sub-activity to get started</p>
                </div>
              ) : (
                <div className="rounded-xl">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-gradient-to-r from-blue-50 to-indigo-50 text-slate-700 border-b border-slate-200">
                        <th className="px-4 py-4 text-left font-semibold text-slate-800 w-1/4 border-r border-slate-200">
                          Activity
                        </th>
                        <th className="px-4 py-4 text-left font-semibold text-slate-800 w-1/2 border-r border-slate-200">
                          Name
                        </th>
                        <th className="px-4 py-4 text-left font-semibold text-slate-800 w-1/6 border-r border-slate-200">
                          Created
                        </th>
                        <th className="px-4 py-4 text-right font-semibold text-slate-800 w-20">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {subActivities.map((subActivity, index) => (
                        <tr
                          key={subActivity.id}
                          className={`border-b border-slate-200 transition-all duration-200 ${
                            index % 2 === 0 ? "bg-white" : "bg-slate-50"
                          } hover:bg-blue-50`}
                        >
                          <td className="px-4 py-4 font-medium text-slate-900 border-r border-slate-200">
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-pink-500 to-pink-600 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                                {subActivity.activity_name ? subActivity.activity_name[0].toUpperCase() : "A"}
                              </div>
                              <span className="font-semibold truncate">{subActivity.activity_name || "Unknown"}</span>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-slate-700 border-r border-slate-200">
                            <div className="text-sm break-words">{subActivity.name}</div>
                          </td>
                          <td className="px-4 py-4 text-slate-500 border-r border-slate-200">
                            <div className="text-sm truncate">
                              {new Date(subActivity.created_at).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              })}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-right">
                            <div className="flex justify-end gap-0.5">
                              <button
                                onClick={() => openEditSubActivitiesModal(subActivity)}
                                className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-all duration-200 hover:shadow-sm"
                                title="Edit sub-activity"
                              >
                                <PencilIcon className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => { setShowDeleteSubActivitiesModal(true); setSubActivityToDelete(subActivity); }}
                                className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all duration-200 hover:shadow-sm"
                                title="Delete sub-activity"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Modality Steps Tab */}
      {isAdminOrSuperAdmin && activeTab === "modality-steps" && (
        <div className="w-full">
          {modalityStepsError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 shadow-sm">
              {modalityStepsError}
            </div>
          )}

          {modalityStepsLoading ? (
            <div className="text-center py-12 text-slate-500">
              <div className="flex items-center justify-center gap-3">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span className="text-lg">Loading modality steps...</span>
              </div>
            </div>
          ) : (
            <div className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 rounded-xl shadow-lg p-6 border border-slate-200">
              {modalitySteps.length === 0 ? (
                <div className="p-12 text-center text-slate-500">
                  <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-10 h-10 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <p className="text-xl font-semibold text-slate-900 mb-2">No modality steps found</p>
                  <p className="text-slate-600">Create your first modality step to get started</p>
                </div>
              ) : (
                <div className="rounded-xl">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-gradient-to-r from-blue-50 to-indigo-50 text-slate-700 border-b border-slate-200">
                        <th className="px-4 py-4 text-left font-semibold text-slate-800 w-1/4 border-r border-slate-200">
                          Modality
                        </th>
                        <th className="px-4 py-4 text-left font-semibold text-slate-800 w-1/2 border-r border-slate-200">
                          Name
                        </th>
                        <th className="px-4 py-4 text-left font-semibold text-slate-800 w-1/6 border-r border-slate-200">
                          Created
                        </th>
                        <th className="px-4 py-4 text-right font-semibold text-slate-800 w-20">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {modalitySteps.map((step, index) => (
                        <tr
                          key={step.id}
                          className={`border-b border-slate-200 transition-all duration-200 ${
                            index % 2 === 0 ? "bg-white" : "bg-slate-50"
                          } hover:bg-blue-50`}
                        >
                          <td className="px-4 py-4 font-medium text-slate-900 border-r border-slate-200">
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                                {step.modality_name ? step.modality_name[0].toUpperCase() : "M"}
                              </div>
                              <span className="font-semibold truncate">{step.modality_name || "Unknown"}</span>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-slate-700 border-r border-slate-200">
                            <div className="text-sm break-words">{step.name}</div>
                          </td>
                          <td className="px-4 py-4 text-slate-500 border-r border-slate-200">
                            <div className="text-sm truncate">
                              {new Date(step.created_at).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              })}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-right">
                            <div className="flex justify-end gap-0.5">
                              <button
                                onClick={() => openEditModalityStepsModal(step)}
                                className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-all duration-200 hover:shadow-sm"
                                title="Edit modality step"
                              >
                                <PencilIcon className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => { setShowDeleteModalityStepsModal(true); setModalityStepToDelete(step); }}
                                className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all duration-200 hover:shadow-sm"
                                title="Delete modality step"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Documentation Methods Tab */}
      {activeTab === "documentation-methods" && (
        <div className="w-full">
          {documentationMethodsError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 shadow-sm">
              {documentationMethodsError}
            </div>
          )}

          {documentationMethodsLoading ? (
            <div className="text-center py-12 text-slate-500">
              <div className="flex items-center justify-center gap-3">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span className="text-lg">Loading documentation methods...</span>
              </div>
            </div>
          ) : (
            <div className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 rounded-xl shadow-lg p-6 border border-slate-200">
              {documentationMethods.length === 0 ? (
                <div className="p-12 text-center text-slate-500">
                  <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-10 h-10 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <p className="text-xl font-semibold text-slate-900 mb-2">No documentation methods found</p>
                  <p className="text-slate-600">Create your first documentation method to get started</p>
                </div>
              ) : (
                <div className="rounded-xl">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gradient-to-r from-blue-50 to-indigo-50 text-slate-700 border-b border-slate-200">
                        <th className="px-4 py-4 text-left font-semibold text-slate-800 w-1/4">
                          Name
                        </th>
                        <th className="px-4 py-4 text-left font-semibold text-slate-800 w-1/2">
                          Session Instructions
                        </th>
                        <th className="px-4 py-4 text-left font-semibold text-slate-800 w-1/6">
                          Created
                        </th>
                        <th className="px-4 py-4 text-right font-semibold text-slate-800 w-20">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {documentationMethods.map((method, index) => (
                        <tr
                          key={method.id}
                          className={`border-b border-slate-100 transition-all duration-200 ${
                            index % 2 === 0 ? "bg-white" : "bg-slate-50"
                          } hover:bg-blue-50 hover:shadow-sm cursor-pointer`}
                          onClick={() => openViewDocumentationMethodModal(method)}
                        >
                          <td className="px-4 py-4 font-medium text-slate-900">
                            <div className="flex items-center gap-2">
                              {/* Icon updated 2025-11-11 - CYAN COLOR */}
                              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-cyan-500 to-teal-600 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                                {method.name ? method.name[0].toUpperCase() : "D"}
                              </div>
                              <span className="font-semibold truncate">{method.name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-slate-700 max-w-xs">
                            <div className="text-sm break-words overflow-hidden">
                              {method.session_instructions 
                                ? (method.session_instructions.length > 50 
                                    ? method.session_instructions.substring(0, 50) + "..." 
                                    : method.session_instructions)
                                : "—"}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-slate-500">
                            <div className="text-sm truncate">
                              {new Date(method.created_at).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              })}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-right">
                            <div className="flex justify-end gap-0.5">
                              <button
                                onClick={(e) => { e.stopPropagation(); openEditDocumentationMethodModal(method); }}
                                className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-all duration-200 hover:shadow-sm"
                                title="Edit documentation method"
                              >
                                <PencilIcon className="w-4 h-4" />
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); setShowDeleteDocumentationMethodsModal(true); setDocumentationMethodToDelete(method); }}
                                className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all duration-200 hover:shadow-sm"
                                title="Delete documentation method"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      <CopingSkillsModals
        showCopingSkillsModal={showCopingSkillsModal}
        setShowCopingSkillsModal={setShowCopingSkillsModal}
        showEditCopingSkillsModal={showEditCopingSkillsModal}
        setShowEditCopingSkillsModal={setShowEditCopingSkillsModal}
        showDeleteCopingSkillsModal={showDeleteCopingSkillsModal}
        setShowDeleteCopingSkillsModal={setShowDeleteCopingSkillsModal}
        copingSkillsForm={copingSkillsForm}
        setCopingSkillsForm={setCopingSkillsForm}
        copingSkillsFormLoading={copingSkillsFormLoading}
        copingSkillsFormError={copingSkillsFormError}
        handleCreateCopingSkill={handleCreateCopingSkill}
        handleUpdateCopingSkill={handleUpdateCopingSkill}
        handleDeleteCopingSkill={handleDeleteCopingSkill}
        selectedCopingSkill={selectedCopingSkill}
        copingSkillToDelete={copingSkillToDelete}
        setCopingSkillToDelete={setCopingSkillToDelete}
      />

      <ClinicalSpecialtiesModals
        showClinicalSpecialtiesModal={showClinicalSpecialtiesModal}
        setShowClinicalSpecialtiesModal={setShowClinicalSpecialtiesModal}
        showEditClinicalSpecialtiesModal={showEditClinicalSpecialtiesModal}
        setShowEditClinicalSpecialtiesModal={setShowEditClinicalSpecialtiesModal}
        showDeleteClinicalSpecialtiesModal={showDeleteClinicalSpecialtiesModal}
        setShowDeleteClinicalSpecialtiesModal={setShowDeleteClinicalSpecialtiesModal}
        clinicalSpecialtiesForm={clinicalSpecialtiesForm}
        setClinicalSpecialtiesForm={setClinicalSpecialtiesForm}
        clinicalSpecialtiesFormLoading={clinicalSpecialtiesFormLoading}
        clinicalSpecialtiesFormError={clinicalSpecialtiesFormError}
        handleCreateClinicalSpecialty={handleCreateClinicalSpecialty}
        handleUpdateClinicalSpecialty={handleUpdateClinicalSpecialty}
        handleDeleteClinicalSpecialty={handleDeleteClinicalSpecialty}
        selectedClinicalSpecialty={selectedClinicalSpecialty}
        clinicalSpecialtyToDelete={clinicalSpecialtyToDelete}
        setClinicalSpecialtyToDelete={setClinicalSpecialtyToDelete}
      />

      <DocumentationMethodsModals
        showDocumentationMethodsModal={showDocumentationMethodsModal}
        setShowDocumentationMethodsModal={setShowDocumentationMethodsModal}
        showEditDocumentationMethodsModal={showEditDocumentationMethodsModal}
        setShowEditDocumentationMethodsModal={setShowEditDocumentationMethodsModal}
        showDeleteDocumentationMethodsModal={showDeleteDocumentationMethodsModal}
        setShowDeleteDocumentationMethodsModal={setShowDeleteDocumentationMethodsModal}
        showViewDocumentationMethodModal={showViewDocumentationMethodModal}
        setShowViewDocumentationMethodModal={setShowViewDocumentationMethodModal}
        documentationMethodsForm={documentationMethodsForm}
        setDocumentationMethodsForm={setDocumentationMethodsForm}
        documentationMethodsFormLoading={documentationMethodsFormLoading}
        documentationMethodsFormError={documentationMethodsFormError}
        handleCreateDocumentationMethod={handleCreateDocumentationMethod}
        handleUpdateDocumentationMethod={handleUpdateDocumentationMethod}
        handleDeleteDocumentationMethod={handleDeleteDocumentationMethod}
        selectedDocumentationMethod={selectedDocumentationMethod}
        setSelectedDocumentationMethod={setSelectedDocumentationMethod}
        selectedDocumentationMethodForView={selectedDocumentationMethodForView}
        documentationMethodToDelete={documentationMethodToDelete}
        setDocumentationMethodToDelete={setDocumentationMethodToDelete}
      />

      <ModalityModals
        showModalityModal={showModalityModal}
        setShowModalityModal={setShowModalityModal}
        showEditModalityModal={showEditModalityModal}
        setShowEditModalityModal={setShowEditModalityModal}
        showDeleteModalityModal={showDeleteModalityModal}
        setShowDeleteModalityModal={setShowDeleteModalityModal}
        showViewModalityModal={showViewModalityModal}
        setShowViewModalityModal={setShowViewModalityModal}
        modalityForm={modalityForm}
        setModalityForm={setModalityForm}
        modalityFormLoading={modalityFormLoading}
        modalityFormError={modalityFormError}
        handleCreateModality={handleCreateModality}
        handleUpdateModality={handleUpdateModality}
        handleDeleteModality={handleDeleteModality}
        selectedModality={selectedModality}
        setSelectedModality={setSelectedModality}
        selectedModalityForView={selectedModalityForView}
        modalityToDelete={modalityToDelete}
        setModalityToDelete={setModalityToDelete}
      />

      <ModalityStepsModals
        showModalityStepsModal={showModalityStepsModal}
        setShowModalityStepsModal={setShowModalityStepsModal}
        showEditModalityStepsModal={showEditModalityStepsModal}
        setShowEditModalityStepsModal={setShowEditModalityStepsModal}
        showDeleteModalityStepsModal={showDeleteModalityStepsModal}
        setShowDeleteModalityStepsModal={setShowDeleteModalityStepsModal}
        modalityStepsForm={modalityStepsForm}
        setModalityStepsForm={setModalityStepsForm}
        modalityStepsFormLoading={modalityStepsFormLoading}
        modalityStepsFormError={modalityStepsFormError}
        handleCreateModalitySteps={handleCreateModalitySteps}
        handleUpdateModalitySteps={handleUpdateModalitySteps}
        handleDeleteModalitySteps={handleDeleteModalitySteps}
        selectedModalityStep={selectedModalityStep}
        modalityStepToDelete={modalityStepToDelete}
        setModalityStepToDelete={setModalityStepToDelete}
        modalities={modalities}
      />

      <ActivityModals
        showActivityModal={showActivityModal}
        setShowActivityModal={setShowActivityModal}
        showEditActivityModal={showEditActivityModal}
        setShowEditActivityModal={setShowEditActivityModal}
        showDeleteActivityModal={showDeleteActivityModal}
        setShowDeleteActivityModal={setShowDeleteActivityModal}
        showViewActivityModal={showViewActivityModal}
        setShowViewActivityModal={setShowViewActivityModal}
        activityForm={activityForm}
        setActivityForm={setActivityForm}
        activityFormLoading={activityFormLoading}
        activityFormError={activityFormError}
        handleCreateActivity={handleCreateActivity}
        handleUpdateActivity={handleUpdateActivity}
        handleDeleteActivity={handleDeleteActivity}
        selectedActivity={selectedActivity}
        setSelectedActivity={setSelectedActivity}
        selectedActivityForView={selectedActivityForView}
        activityToDelete={activityToDelete}
        setActivityToDelete={setActivityToDelete}
      />

      <SubActivitiesModals
        showSubActivitiesModal={showSubActivitiesModal}
        setShowSubActivitiesModal={setShowSubActivitiesModal}
        showEditSubActivitiesModal={showEditSubActivitiesModal}
        setShowEditSubActivitiesModal={setShowEditSubActivitiesModal}
        showDeleteSubActivitiesModal={showDeleteSubActivitiesModal}
        setShowDeleteSubActivitiesModal={setShowDeleteSubActivitiesModal}
        subActivitiesForm={subActivitiesForm}
        setSubActivitiesForm={setSubActivitiesForm}
        subActivitiesFormLoading={subActivitiesFormLoading}
        subActivitiesFormError={subActivitiesFormError}
        handleCreateSubActivities={handleCreateSubActivities}
        handleUpdateSubActivities={handleUpdateSubActivities}
        handleDeleteSubActivities={handleDeleteSubActivities}
        selectedSubActivity={selectedSubActivity}
        subActivityToDelete={subActivityToDelete}
        setSubActivityToDelete={setSubActivityToDelete}
        activities={activities}
      />
    </div>
  );
};

export default Settings;
